import React, { useState, useEffect } from 'react'
import { WordGrid } from './WordGrid'
import { SongState, WindowMessage, AppSettings } from '../types'
import { useDisplayWindowPlatform } from '../platform/DisplayWindowPlatformProvider'

export const DisplayWindow: React.FC = () => {
	const platform = useDisplayWindowPlatform()
	const [songState, setSongState] = useState<SongState | null>(null)
	const [settings, setSettings] = useState<AppSettings>({
		hideWordMode: 'hide',
		showWordMode: 'word',
		fontSize: 48,
		currentPageIndex: 0,
	})

	const handleWordToggle = (wordId: string) => {
		console.log('Display window: toggling word', wordId)
		// Send message back to main window using platform adapter
		platform.sendToMain({
			type: 'TOGGLE_WORD_FROM_DISPLAY',
			payload: { wordId },
		})
	}

	useEffect(() => {
		const handleMessage = (message: WindowMessage) => {
			const { type, payload } = message

			switch (type) {
				case 'UPDATE_SONG':
					if (payload.songData) {
						setSongState(payload.songData)
						setSettings(prevSettings => ({
							...prevSettings,
							currentPageIndex: payload.currentPageIndex !== undefined ? payload.currentPageIndex : prevSettings.currentPageIndex,
						}))
						document.title = payload.songData.title
					}
					break

				case 'UPDATE_WORD':
					if (payload.wordId && payload.wordState && songState) {
						setSongState(prevSong => {
							if (!prevSong) return prevSong

							const newPages = prevSong.pages.map(page =>
								page.map(line =>
									line.map(word =>
										word.id === payload.wordId
											? { ...word, ...payload.wordState }
											: word,
									),
								),
							)

							return {
								...prevSong,
								pages: newPages,
							}
						})
					}
					break

				case 'UPDATE_SETTINGS':
					setSettings(prevSettings => ({
						...prevSettings,
						...payload,
					}))
					break
			}
		}

		// Use platform adapter to listen for messages from main window
		const cleanup = platform.onMessageFromMain(handleMessage)

		return cleanup
	}, [platform, songState, settings])

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'ArrowRight') {
				platform.sendToMain({
					type: 'NAVIGATE_PAGE',
					payload: { direction: 'next' },
				})
			} else if (event.key === 'ArrowLeft') {
				platform.sendToMain({
					type: 'NAVIGATE_PAGE',
					payload: { direction: 'prev' },
				})
			} else if (event.key === 'ArrowUp') {
				platform.sendToMain({
					type: 'UPDATE_HIDDEN_PERCENTAGE',
					payload: { direction: 'increase' },
				})
			} else if (event.key === 'ArrowDown') {
				platform.sendToMain({
					type: 'UPDATE_HIDDEN_PERCENTAGE',
					payload: { direction: 'decrease' },
				})
			} else if (event.key === 'Enter') {
				platform.sendToMain({
					type: 'TOGGLE_HIDDEN_PERCENTAGE',
					payload: {},
				})
			}
		}

		window.addEventListener('keydown', handleKeyDown)

		return () => {
			window.removeEventListener('keydown', handleKeyDown)
		}
	}, [platform])

	useEffect(() => {
		// Send ready message to parent after a short delay to ensure parent is ready
		const timer = setTimeout(() => {
			platform.sendToMain({ type: 'DISPLAY_READY', payload: {} })
		}, 10)

		return () => {
			clearTimeout(timer)
		}
	}, [])

	if (!songState) {
		return (
			<div className="display-window display-window--empty">
				<div className="display-message">
					<h2>Waiting for lyrics...</h2>
					<p>Paste some lyrics in the control window to get started!</p>
				</div>
			</div>
		)
	}

	return (
		<div className="display-window">
			{songState && songState.pages && songState.pages[settings.currentPageIndex] && (
				<WordGrid
					lines={songState.pages[settings.currentPageIndex]}
					hideWordMode={settings.hideWordMode}
					showWordMode={settings.showWordMode}
					onWordToggle={handleWordToggle}
					fontSize={settings.fontSize}
				/>
			)}
			<div className="page-navigation-overlay">
				<button
					onClick={() => platform.sendToMain({ type: 'NAVIGATE_PAGE', payload: { direction: 'prev' } })}
					disabled={settings.currentPageIndex === 0}
					className="btn btn-nav btn-nav-left"
				>
					⬅
				</button>
				<button
					onClick={() => platform.sendToMain({ type: 'NAVIGATE_PAGE', payload: { direction: 'next' } })}
					disabled={songState && settings.currentPageIndex === songState.pages.length - 1}
					className="btn btn-nav btn-nav-right"
				>
					⮕
				</button>
			</div>
		</div>
	)
}
